import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Info,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useAccount } from "@/contexts/account.context";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { toast } from "@/hooks/use-toast";
import { ehAcessoNegado, mensagemDeErro } from "@/lib/team-errors";
import teamService, {
  type TeamOverviewResponse,
  type TeamOverviewStore,
} from "@/services/team.service";
import {
  formatCurrency,
  formatDecimal,
  formatInteger,
  formatPercent,
  formatSignedPercent,
} from "@/utils/numberFormat";

type Consolidado = TeamOverviewResponse["data"];

interface TeamOverviewPanelProps {
  teamId: string;
}

/**
 * O painel consolidado do time — a visão macro.
 *
 * ## O que se soma, e o que não se soma
 *
 * Nada é recalculado aqui: os totais vêm prontos de `GET /teams/{id}/overview`, que soma os
 * contadores e o dinheiro e **recalcula** as razões (ROAS, taxa de conversão, ticket médio,
 * `marketing`) sobre os agregados. Somar razão é o defeito clássico — a média dos ROAS de
 * cinco lojas não é o ROAS do time, porque a que gastou dez reais pesaria igual à que gastou
 * dez mil. A tela apenas formata.
 *
 * Por isso também não há rodapé calculado na tabela de lojas: as colunas de razão não se
 * somam, e o total do time já está nos cartões acima.
 *
 * ## Variação
 *
 * Os cartões do time **não** têm variação, e a ausência é do contrato: variação também é
 * razão, e reconstruí-la exigiria os totais da janela anterior, que o card de uma loja não
 * publica. Cada linha de loja carrega a variação dela, que vem de graça — e é ela que a
 * tabela mostra.
 *
 * ## Uma loja que falha não derruba o painel
 *
 * `status` diz o que houve com cada loja, e só `ok` entrou nos totais. O cabeçalho publica
 * quantas responderam de quantas, para que um total menor do que o esperado tenha
 * explicação em tela em vez de parecer queda de resultado.
 */
export const TeamOverviewPanel = ({ teamId }: TeamOverviewPanelProps) => {
  const navigate = useNavigate();
  const { accounts, switchAccount } = useAccount();
  const { filter, isInitialized } = useGlobalFilter();

  const [dados, setDados] = useState<Consolidado | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const ultimaConsultaRef = useRef<string | null>(null);

  const carregar = useCallback(async () => {
    const startDate = formatDateToISO(filter.dateRange.startDate);
    const endDate = formatDateToISO(filter.dateRange.endDate);
    const comparison = filter.comparison?.type ?? null;

    // O fan-out custa uma requisição por loja no servidor: repetir a mesma janela porque um
    // efeito rodou de novo é caro de verdade, e não só redundante.
    const assinatura = `${teamId}|${startDate}|${endDate}|${comparison}`;
    if (ultimaConsultaRef.current === assinatura) return;

    setLoading(true);
    try {
      const resposta = await teamService.getOverview(teamId, {
        start_date: startDate,
        end_date: endDate,
        comparison_type: comparison,
      });
      setDados(resposta);
      setErro(null);
      ultimaConsultaRef.current = assinatura;
    } catch (error) {
      setErro(
        ehAcessoNegado(error)
          ? "Você não tem acesso ao painel deste time."
          : mensagemDeErro(error, "Não foi possível carregar o consolidado."),
      );
    } finally {
      setLoading(false);
    }
  }, [teamId, filter]);

  useEffect(() => {
    if (!isInitialized) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    carregar();
  }, [carregar, isInitialized]);

  /**
   * Entrar na loja a partir da linha.
   *
   * Quem administra a agência troca de conta de verdade (token da subconta); o membro só
   * troca o tenant. `switchAccount` já sabe a diferença, e a tela só precisa mandar o id da
   * loja — desde que ela esteja na lista que o contexto conhece.
   */
  const abrirLoja = async (loja: TeamOverviewStore) => {
    const conhecida = accounts.some((conta) => conta.id === loja.account_id);
    if (!conhecida) {
      toast({
        title: "Loja indisponível",
        description:
          "Esta loja não está entre as que você pode abrir com este acesso.",
        variant: "destructive",
      });
      return;
    }
    // A troca é aguardada, e não disparada: para quem administra ela passa por
    // `POST /accounts/switch-account` e só grava o token e o `active_customer_uuid`
    // quando a resposta chega. Navegar antes montaria o painel com o tenant da loja
    // **anterior** — os números de uma loja sob o nome de outra.
    await switchAccount(loja.account_id, { redirect: false });
    navigate("/dashboard");
  };

  return (
    <div className="space-y-6">
      <GlobalFilter />

      {erro ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-amber-500" />
            <p className="font-medium">{erro}</p>
          </CardContent>
        </Card>
      ) : loading || !dados ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, indice) => (
              <Skeleton key={indice} className="h-24 w-full" />
            ))}
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <>
          <Cobertura dados={dados} />
          <Totais dados={dados} />
          <TabelaDeLojas dados={dados} onAbrir={abrirLoja} />
        </>
      )}
    </div>
  );
};

/** Quantas lojas responderam, de quantas — o rodapé de confiança dos números acima. */
const Cobertura = ({ dados }: { dados: Consolidado }) => {
  const naoConsultadas = dados.stores.filter(
    (loja) => loja.status === "nao_consultada",
  ).length;
  const falharam = dados.stores.filter(
    (loja) => loja.status === "falhou",
  ).length;
  const semDados = dados.stores.filter(
    (loja) => loja.status === "sem_dados",
  ).length;
  const parcial = dados.stores_answered < dados.stores_total;

  return (
    <div
      className={`flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border p-3 text-sm ${
        parcial
          ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200"
          : "text-muted-foreground"
      }`}
    >
      <span className="flex items-center gap-2">
        <Info className="h-4 w-4 shrink-0" />
        <span>
          <strong>{dados.stores_answered}</strong> de{" "}
          <strong>{dados.stores_total}</strong>{" "}
          {dados.stores_total === 1 ? "loja entrou" : "lojas entraram"} nos
          totais.
        </span>
      </span>
      {falharam > 0 && <span>{falharam} não respondeu ao provedor.</span>}
      {semDados > 0 && <span>{semDados} sem integração para consultar.</span>}
      {naoConsultadas > 0 && (
        <span>
          {naoConsultadas} acima do limite do consolidado — abra uma a uma.
        </span>
      )}
    </div>
  );
};

interface CartaoProps {
  rotulo: string;
  valor: string;
  detalhe?: string;
}

const Cartao = ({ rotulo, valor, detalhe }: CartaoProps) => (
  <Card>
    <CardContent className="p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">
        {rotulo}
      </p>
      <p className="mt-1 text-2xl font-semibold">{valor}</p>
      {detalhe && <p className="text-xs text-muted-foreground">{detalhe}</p>}
    </CardContent>
  </Card>
);

const Totais = ({ dados }: { dados: Consolidado }) => {
  const t = dados.totals;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Cartao
          rotulo="Investimento"
          valor={formatCurrency(t.total_investment)}
        />
        <Cartao rotulo="Receita" valor={formatCurrency(t.total_revenue)} />
        <Cartao rotulo="ROAS" valor={formatDecimal(t.roas)} />
        <Cartao
          rotulo="Investimento / receita"
          valor={formatPercent(t.marketing_percentage)}
          detalhe="Quanto da receita voltou para mídia"
        />
        <Cartao
          rotulo="Transações"
          valor={formatInteger(t.total_transactions)}
        />
        <Cartao
          rotulo="Ticket médio"
          valor={formatCurrency(t.average_order_value)}
        />
        <Cartao rotulo="Sessões" valor={formatInteger(t.sessions)} />
        <Cartao
          rotulo="Taxa de conversão"
          valor={formatPercent(t.conversion_rate)}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Cartao rotulo="Pedidos" valor={formatInteger(t.orders)} />
        <Cartao rotulo="Checkouts" valor={formatInteger(t.checkouts)} />
        <Cartao
          rotulo="Compradores"
          valor={formatInteger(t.total_purchasers)}
          detalhe={`${formatInteger(t.first_time_purchasers)} pela primeira vez`}
        />
        <Cartao
          rotulo="Projeção de receita"
          valor={formatCurrency(t.revenue_projection)}
          detalhe={`Investimento projetado: ${formatCurrency(t.investment_projection)}`}
        />
      </div>
    </div>
  );
};

/** A etiqueta de cada linha da tabela — o que houve com a loja. */
const ETIQUETA_DE_STATUS: Record<
  string,
  { texto: string; classe: string } | undefined
> = {
  ok: { texto: "Nos totais", classe: "bg-emerald-100 text-emerald-800" },
  falhou: { texto: "Sem resposta", classe: "bg-red-100 text-red-800" },
  sem_dados: { texto: "Sem integração", classe: "bg-gray-100 text-gray-800" },
  nao_consultada: {
    texto: "Não consultada",
    classe: "bg-amber-100 text-amber-900",
  },
};

/** O que se mostra de um `status` que esta versão da tela não conhece. */
const ETIQUETA_DESCONHECIDA = {
  texto: "Fora dos totais",
  classe: "bg-gray-100 text-gray-800",
};

/** A variação da loja, que é a única variação que o consolidado tem. */
const Variacao = ({ valor }: { valor: number }) => {
  if (!Number.isFinite(valor) || valor === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const subiu = valor > 0;
  const Icone = subiu ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 ${
        subiu ? "text-emerald-600" : "text-red-600"
      }`}
    >
      <Icone className="h-3.5 w-3.5" />
      {formatSignedPercent(valor)}
    </span>
  );
};

const TabelaDeLojas = ({
  dados,
  onAbrir,
}: {
  dados: Consolidado;
  onAbrir: (loja: TeamOverviewStore) => Promise<void>;
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-base">Lojas do time</CardTitle>
      <CardDescription>
        Cada linha traz a variação da própria loja contra o período de
        comparação. As colunas de razão não se somam — o total do time está nos
        cartões acima.
      </CardDescription>
    </CardHeader>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 text-left font-medium">Loja</th>
              <th className="px-4 py-3 text-right font-medium">Investimento</th>
              <th className="px-4 py-3 text-right font-medium">Receita</th>
              <th className="px-4 py-3 text-right font-medium">ROAS</th>
              <th className="px-4 py-3 text-right font-medium">Transações</th>
              <th className="px-4 py-3 text-right font-medium">Sessões</th>
              <th className="px-4 py-3 text-right font-medium">Conversão</th>
              <th className="px-4 py-3 text-right font-medium">Ticket médio</th>
              <th className="px-4 py-3 text-right font-medium">
                Receita vs. anterior
              </th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {dados.stores.map((loja) => {
              // Busca com reserva, e não índice direto: a união de `status` só existe em
              // tempo de compilação, e um valor novo do servidor derrubaria a renderização
              // inteira — numa tela cuja premissa é justamente que uma loja não derruba o
              // painel.
              const etiqueta =
                ETIQUETA_DE_STATUS[loja.status] ?? ETIQUETA_DESCONHECIDA;
              const consultada = loja.status === "ok";

              return (
                <tr
                  key={loja.account_id}
                  className={consultada ? "" : "text-muted-foreground"}
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">{loja.name}</span>
                      <Badge
                        variant="outline"
                        className={`w-fit border-0 text-2xs ${etiqueta.classe}`}
                      >
                        {etiqueta.texto}
                      </Badge>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {consultada ? formatCurrency(loja.total_investment) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {consultada ? formatCurrency(loja.total_revenue) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {consultada ? formatDecimal(loja.roas) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {consultada ? formatInteger(loja.total_transactions) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {consultada ? formatInteger(loja.sessions) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {consultada ? formatPercent(loja.conversion_rate) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {consultada
                      ? formatCurrency(loja.average_order_value)
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {consultada ? (
                      <Variacao valor={loja.revenue_change} />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {loja.customer_uuid && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void onAbrir(loja)}
                      >
                        Abrir
                        <ArrowRight className="ml-1 h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {dados.stores.length === 0 && (
              <tr>
                <td
                  colSpan={10}
                  className="px-4 py-10 text-center text-muted-foreground"
                >
                  Este time ainda não atende nenhuma loja.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

export default TeamOverviewPanel;
